/**
 * Time presentation (OCP-536).
 *
 * THE RULE: store and compute in UTC, present in Europe/Berlin with the zone
 * named. No database column, API payload or server clock changes - timestamptz
 * and ISO-8601 UTC stay the storage and wire format. Only rendering goes
 * through here. Do NOT "fix" a surface by writing local times into the
 * database or by adding an offset: the IANA zone is what makes DST correct.
 *
 * Why: a key created at 10:09 CEST showed up as 08:09 in a UTC listing and
 * was briefly suspected to be a foreign login (2026-07-26). Mixed clocks -
 * hard UTC here, raw ISO there, unlabelled browser-local elsewhere - are
 * where mistakes happen, and the maintenance scheduler is the place where a
 * mistake is expensive.
 *
 * Output shapes (all zone-labelled, sortable, no `T`, no trailing `Z`):
 *   fmtDateTime(iso)          2026-09-07 04:15 CEST
 *   fmtDateTime(iso, {seconds: true})   2026-09-07 04:15:26 CEST
 *   fmtDate(iso)              2026-09-07            (a calendar day in Berlin)
 *   fmtTime(iso)              04:15 CEST
 *   fmtRelative(iso, now)     3 min ago / in 2 h  (unlabelled on purpose -
 *                             relative to the reader, zone-free)
 *
 * `timeZone` defaults to Europe/Berlin; the maintenance surfaces pass the
 * policy zone (`maintenance.timezone`, OCP-527) so they follow the policy
 * rather than a second constant.
 */

export const DISPLAY_TIME_ZONE = 'Europe/Berlin';

export interface FormatTimeOptions {
  /** IANA zone. Default Europe/Berlin. */
  timeZone?: string;
  /** Include seconds. Default false. */
  seconds?: boolean;
}

type DateInput = string | number | Date | null | undefined;

function toDate(input: DateInput): Date | null {
  if (input === null || input === undefined || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parts(d: Date, timeZone: string, seconds: boolean): Record<string, string> {
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
    hour12: false,
    timeZoneName: 'short',
  });
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(d)) out[p.type] = p.value;
  // en-GB renders 24:00 for midnight in some engines; normalise.
  if (out.hour === '24') out.hour = '00';
  return out;
}

/** Zone-labelled short name, e.g. CEST / CET / UTC (Intl may give GMT+2 for exotic zones). */
export function zoneLabel(input: DateInput, timeZone = DISPLAY_TIME_ZONE): string {
  const d = toDate(input) ?? new Date();
  return parts(d, timeZone, false).timeZoneName ?? timeZone;
}

/** `2026-09-07 04:15 CEST` */
export function fmtDateTime(input: DateInput, opts: FormatTimeOptions = {}): string {
  const d = toDate(input);
  if (!d) return '-';
  const p = parts(d, opts.timeZone ?? DISPLAY_TIME_ZONE, opts.seconds === true);
  const hm = `${p.hour}:${p.minute}${opts.seconds ? `:${p.second}` : ''}`;
  return `${p.year}-${p.month}-${p.day} ${hm} ${p.timeZoneName}`;
}

/** `2026-09-07` - the calendar day in the display zone (a UTC 23:30 is the next day in Berlin). */
export function fmtDate(input: DateInput, opts: Pick<FormatTimeOptions, 'timeZone'> = {}): string {
  const d = toDate(input);
  if (!d) return '-';
  const p = parts(d, opts.timeZone ?? DISPLAY_TIME_ZONE, false);
  return `${p.year}-${p.month}-${p.day}`;
}

/** `04:15 CEST` */
export function fmtTime(input: DateInput, opts: FormatTimeOptions = {}): string {
  const d = toDate(input);
  if (!d) return '-';
  const p = parts(d, opts.timeZone ?? DISPLAY_TIME_ZONE, opts.seconds === true);
  return `${p.hour}:${p.minute}${opts.seconds ? `:${p.second}` : ''} ${p.timeZoneName}`;
}

/** `3 min ago` / `in 2 h` / `just now` - relative to `now`, zone-free by nature. */
export function fmtRelative(input: DateInput, now: DateInput = new Date()): string {
  const d = toDate(input);
  const n = toDate(now);
  if (!d || !n) return '-';
  const diff = d.getTime() - n.getTime();
  const abs = Math.abs(diff);
  const past = diff < 0;
  const unit =
    abs < 45_000 ? null
    : abs < 3_600_000 ? [Math.round(abs / 60_000), 'min'] as const
    : abs < 86_400_000 ? [Math.round(abs / 3_600_000), 'h'] as const
    : [Math.round(abs / 86_400_000), 'd'] as const;
  if (!unit) return 'just now';
  return past ? `${unit[0]} ${unit[1]} ago` : `in ${unit[0]} ${unit[1]}`;
}

/**
 * The value a `<input type="datetime-local">` needs for an instant, in the
 * display zone (`YYYY-MM-DDTHH:mm`). The browser's own datetime-local
 * semantics are the reader's OS zone; use this only where the page also
 * converts back with `datetimeLocalToIso`, so both directions use the same
 * zone and a scheduled instant round-trips unchanged.
 */
export function isoToDatetimeLocal(input: DateInput, timeZone = DISPLAY_TIME_ZONE): string {
  const d = toDate(input);
  if (!d) return '';
  const p = parts(d, timeZone, false);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/**
 * Inverse of `isoToDatetimeLocal`: interpret a `YYYY-MM-DDTHH:mm` wall-clock
 * value in `timeZone` and return the UTC instant as ISO. DST is resolved by
 * the zone: the offset is computed for that wall-clock, not assumed.
 */
export function datetimeLocalToIso(value: string, timeZone = DISPLAY_TIME_ZONE): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  // First guess: treat the wall clock as UTC, then correct by the zone's
  // offset at that instant; iterate once more so a DST boundary settles.
  let guess = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? '0'));
  for (let i = 0; i < 2; i += 1) {
    const p = parts(new Date(guess), timeZone, true);
    const wallAsUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
    const offset = wallAsUtc - guess;
    guess = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? '0')) - offset;
  }
  return new Date(guess).toISOString();
}
